import * as React from 'react';

import { useApolloClient } from '@apollo/client';

import type { Learner } from 'bundles/enterprise-admin-messaging-v2/components/AIMessageModal/types';
import EnterpriseLearnerCohortCreateAndFetchDoc from 'bundles/enterprise-admin-messaging-v2/mutations/EnterpriseLearnerCohortCreateAndFetch.graphql';
import type {
  EnterpriseLearnerCohortCreateAndFetchMutation,
  EnterpriseLearnerCohortCreateAndFetchMutationVariables,
} from 'bundles/enterprise-admin-messaging-v2/mutations/__generated__/EnterpriseLearnerCohortCreateAndFetch';

export type CohortCreateStatus = 'SUCCESS' | 'DISAMBIGUATION_NEEDED' | 'ERROR';

export type CohortCreateResult =
  | {
      status: 'SUCCESS';
      cohortId: string;
      learners: Learner[];
      nextPageToken: number | null;
      resolvedProgramIds: string[];
      warnings: string[];
    }
  | { status: 'DISAMBIGUATION_NEEDED'; warnings: string[] }
  | { status: 'ERROR'; errorCode: string; errorMessage: string };

const NLQ_PREFIX = 'Admin is sending an email to the learners, fetch the learner cohort corresponding to this prompt: ';

const mapLearner = (l: { userId?: string | null; fullName?: string | null; email?: string | null }): Learner => ({
  id: l.email ?? l.userId ?? '',
  name: l.fullName ?? '',
  email: l.email ?? '',
});

/**
 * Data-layer hook wrapping the `EnterpriseLearnerCohort_CreateAndFetch` mutation.
 * Returns a fire-and-forget `createCohort` function and a loading flag. The result
 * is returned as a discriminated union so the caller can branch on status.
 */
export const useCohortCreate = () => {
  const client = useApolloClient();
  const [loading, setLoading] = React.useState(false);

  const createCohort = React.useCallback(
    async (nlq: string, orgId: string, programIds?: string[]): Promise<CohortCreateResult> => {
      setLoading(true);
      try {
        const { data } = await client.mutate<
          EnterpriseLearnerCohortCreateAndFetchMutation,
          EnterpriseLearnerCohortCreateAndFetchMutationVariables
        >({
          mutation: EnterpriseLearnerCohortCreateAndFetchDoc,
          variables: {
            input: {
              nlq: `${NLQ_PREFIX}${nlq}`,
              authContext: { orgId },
              ...(programIds?.length ? { sessionFilters: { programIds } } : {}),
            },
          },
          context: { clientName: 'gatewayGql' },
        });

        const result = data?.EnterpriseLearnerCohort_CreateAndFetch;
        if (!result) {
          return { status: 'ERROR', errorCode: 'EMPTY_RESPONSE', errorMessage: 'No response from cohort API.' };
        }

        switch (result.status) {
          case 'SUCCESS':
            if (!result.cohortId) {
              return {
                status: 'ERROR',
                errorCode: 'MISSING_COHORT_ID',
                errorMessage: 'Cohort created but no ID returned.',
              };
            }
            return {
              status: 'SUCCESS',
              cohortId: result.cohortId,
              learners: result.learners.map(mapLearner),
              nextPageToken: result.nextPageToken ?? null,
              resolvedProgramIds: result.resolvedProgramIds,
              warnings: result.warnings,
            };
          case 'DISAMBIGUATION_NEEDED':
            return { status: 'DISAMBIGUATION_NEEDED', warnings: result.warnings };
          case 'ERROR':
          default:
            return {
              status: 'ERROR',
              errorCode: result.error?.code ?? 'UNKNOWN',
              errorMessage: result.error?.message ?? 'Cohort creation failed.',
            };
        }
      } catch (e) {
        return {
          status: 'ERROR',
          errorCode: 'NETWORK_ERROR',
          errorMessage: e instanceof Error ? e.message : 'Failed to create cohort.',
        };
      } finally {
        setLoading(false);
      }
    },
    [client]
  );

  return { createCohort, loading };
};

export default useCohortCreate;
